import ContentPage from '@/components/feature/ContentPage';

export default function LegitPage() {
  return (
    <ContentPage
      title="Is CSSBuy Legit? Safety, Trust and Buying Guide"
      metaTitle="Is CSSBuy Legit? Complete Safety & Trust Guide 2026 | CSSBuyBR"
      metaDescription="Is CSSBuy legit? Independent analysis of safety, trust, official URLs, account security, payment risks and fake website warnings."
      subtitle="CSSBuy is a known platform, but understanding the risks and protections is essential before using any international shopping service."
      showToc
      sections={[
        { id: 'legitimacy', title: 'Platform Legitimacy' },
        { id: 'official', title: 'Official URLs' },
        { id: 'security', title: 'Account Security' },
        { id: 'payment', title: 'Payment Risks' },
        { id: 'fake', title: 'Fake Websites' },
        { id: 'research', title: 'How to Research' },
        { id: 'conclusion', title: 'Conclusion' },
      ]}
      content={
        <div className="space-y-6">
          <section id="legitimacy">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Is CSSBuy a Legitimate Platform?</h2>
            <p>CSSBuy is a known shopping agent platform that has been used by international buyers for several years. It operates as an intermediary service, facilitating purchases from Chinese marketplaces and handling shipping to customers worldwide.</p>
            <p className="mt-2">However, like any international shopping platform, it is important to understand that:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>The platform itself facilitates transactions but does not guarantee seller quality</li>
              <li>International shipping involves inherent risks and complexities</li>
              <li>Individual experiences may vary based on seller, shipping method, and destination</li>
              <li>Community feedback and research are essential before committing</li>
            </ul>
          </section>

          <section id="official">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Verifying Official URLs</h2>
            <p>Fake websites may try to impersonate CSSBuy to steal your information. Always verify you are on the correct domain:</p>
            <div className="grid sm:grid-cols-2 gap-3 my-4">
              {[
                'Check the URL carefully in your browser address bar',
                'Look for HTTPS and a valid security certificate',
                'Bookmark official pages after verifying them',
                'Avoid clicking links from unsolicited emails or messages',
                'Verify URLs through official social media accounts',
                'Be cautious of domains with misspellings or extra characters',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-background-100 text-sm text-foreground-700">
                  <i className="ri-link text-accent-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section id="security">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Account Security</h2>
            <p>Protecting your account is your responsibility. Follow these practices:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Create a strong, unique password that you do not use elsewhere</li>
              <li>Enable two-factor authentication if the platform offers it</li>
              <li>Never share your login credentials with anyone</li>
              <li>Monitor your account for unauthorized activity</li>
              <li>Keep your email secure — it is the primary recovery method</li>
              <li>Use a password manager to generate and store complex passwords</li>
            </ul>
          </section>

          <section id="payment">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Payment Risks and Protections</h2>
            <p>When making payments through CSSBuy or any international platform:</p>
            <div className="grid sm:grid-cols-2 gap-3 my-4">
              {[
                'Use payment methods with buyer protection when possible',
                'Never send payments outside the official platform',
                'Avoid direct bank transfers to unknown parties',
                'Keep all payment confirmations and transaction records',
                'Be cautious of sellers requesting payment outside the platform',
                'Understand the refund and dispute policies before buying',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-accent-50 text-sm text-foreground-700">
                  <i className="ri-shield-check-line text-accent-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section id="fake">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Fake Websites and Scams</h2>
            <p>Scammers create fake websites and social media accounts to trick users. Warning signs include:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Unusually low prices that seem too good to be true</li>
              <li>Pressure to complete transactions quickly</li>
              <li>Requests for payment through unofficial methods</li>
              <li>Poor website design, grammar errors, or broken functionality</li>
              <li>No clear contact information or customer support</li>
              <li>Domains that look similar but are not exactly the official URL</li>
            </ul>
          </section>

          <section id="research">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">How to Research Before Buying</h2>
            <p>Before committing to any purchase, take time to research:</p>
            <ol className="list-decimal list-inside space-y-1.5 ml-2">
              <li>Search for recent community reviews and experiences</li>
              <li>Check multiple sources for seller feedback</li>
              <li>Compare prices across different sellers and platforms</li>
              <li>Read QC photo threads for the specific products you want</li>
              <li>Understand the total cost including shipping and fees</li>
              <li>Verify the seller\'s return and refund policies</li>
              <li>Start with small test orders before large purchases</li>
            </ol>
          </section>

          <section id="conclusion">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Conclusion</h2>
            <p>CSSBuy is a known platform in the international shopping community, but as with any online service, due diligence is essential. No platform can guarantee the quality of every seller or prevent all issues. Your safety depends on:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Verifying official URLs and avoiding phishing sites</li>
              <li>Researching sellers before committing to purchases</li>
              <li>Using secure payment methods with protections</li>
              <li>Reviewing QC photos before approving shipments</li>
              <li>Understanding shipping risks and customs regulations</li>
              <li>Keeping detailed records of all transactions</li>
            </ul>
            <p className="mt-2">This guide is for informational purposes only. We do not make absolute guarantees about any platform or service. Always conduct your own research before making purchasing decisions.</p>
          </section>

          <div className="mt-6 p-4 rounded-xl bg-primary-50 border border-primary-200/70">
            <h3 className="text-sm font-semibold text-primary-800 mb-2">Continue Researching</h3>
            <p className="text-sm text-primary-700 mb-3">Visit the main site to browse products and verify current information.</p>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors">
              Visit Main Site
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      }
    />
  );
}